import type {
  AvailabilityErrorBody,
  AvailabilityRepeat,
  AvailabilitySeries,
  AvailabilityWriteBody,
  RepeatUnit,
  Weekday,
} from 'features/Schedule/api/availabilitySeries.types'

export const WEEKDAYS: Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export const WEEKDAY_SHORT: Record<Weekday, string> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
}

export const WEEKDAY_LONG: Record<Weekday, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
}

const CHECKBOX_ORDER: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

const EVERY_WORD = ['every', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th']

export const REPEAT_UNITS: RepeatUnit[] = ['day', 'workday', 'week', 'month', 'year']

export type DraftRepeat = 'none' | 'every' | 'weekly_days'

export type DraftUnit = RepeatUnit | 'nth' | 'last'

export type BlockDraft = {
  id: number | null
  userId: number
  kindId: number | null
  title: string
  allDay: boolean
  date: string
  startMinutes: number
  duration: number
  repeat: DraftRepeat
  every: number
  unit: DraftUnit
  weekdays: Weekday[]
  until: string | null
  pref: number[]
  legacyTime: boolean
}

export type NthChoice = {
  key: 'nth' | 'last'
  label: string
  nth: number
  weekday: Weekday
}

export type RowLock = 'open' | 'readonly' | 'unknown' | 'busy'

const ERROR_TEXT: Record<string, string> = {
  not_in_account: 'You do not have access to this schedule.',
  unknown_site: 'Please choose a clinic and try again.',
  target_not_in_account: 'You cannot change the schedule of this person.',
  series_not_in_account: 'You cannot change this block.',
  series_not_on_this_site: 'This block belongs to another clinic. Switch clinic to change it.',
  not_found: 'This block no longer exists. The list will be refreshed.',
  settings_unknown: 'Availability for this clinic is not ready yet. Please try again later or contact support.',
  settings_mismatch: 'Availability for this clinic can only be changed in the EMR for now.',
  kinds_unknown: 'Availability for this clinic is not ready yet. Please try again later or contact support.',
  site_time_zone_missing: 'This clinic has no time zone set. Please contact support.',
  site_time_zone_invalid: 'This clinic has no time zone set. Please contact support.',
  pending_change: 'This block is still being saved to the EMR. Please try again in a few seconds.',
  split_pending: 'This block is still being saved to the EMR. Please try again in a few seconds.',
  stale_row: 'This block was changed in the meantime. The list will be refreshed.',
  push_in_progress: 'This block is being saved to the EMR. Please wait a moment.',
  changed_meanwhile: 'This block changed in the meantime. The list will be refreshed.',
  reserved_placeholder_missing: 'Reserved blocks cannot be saved for this account yet. Please contact support.',
  not_an_occurrence: 'Please pick a day of this block.',
  read_only: 'This block can only be changed in the EMR.',
  unknown_repeat: 'This block can only be changed in the EMR. You can still delete it completely.',
  target_has_no_emr_id: 'This person is not linked to the EMR yet.',
  emr_disabled: 'The EMR is not turned on for this account.',
  site_not_mapped: 'This clinic is not linked to the EMR.',
  not_failed: 'There is nothing to retry.',
  retry_the_original: 'Retry the original block of this split.',
  discard_the_original: 'Discard the original block of this split.',
  outside_window: 'This block is too old to restore from the EMR.',
  openemr_unavailable: 'The EMR cannot be reached right now. Please try again in a few minutes.',
  internal_error: 'Something went wrong. Please try again.',
}

export const checkboxWeekdays = CHECKBOX_ORDER

export const everyOptions = (current: number) => {
  const values = EVERY_WORD.map((_, index) => index + 1)
  if (current > 9 && !values.includes(current)) values.push(current)
  return values.map((value) => ({
    value,
    label: value <= 9 ? EVERY_WORD[value - 1] : `every ${value}`,
  }))
}

export const weekdayOf = (iso: string): Weekday => {
  const [year, month, day] = iso.split('-').map(Number)
  return WEEKDAYS[new Date(year, month - 1, day).getDay()]
}

export const addDays = (iso: string, days: number): string => {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day + days)
  const monthText = String(date.getMonth() + 1).padStart(2, '0')
  const dayText = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${monthText}-${dayText}`
}

export const formatMdy = (iso: string): string => {
  const [year, month, day] = iso.split('-')
  return `${Number(month)}/${Number(day)}/${year}`
}

export const parseClock = (value: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (minutes > 59 || hours > 24 || (hours === 24 && minutes !== 0)) return null
  return hours * 60 + minutes
}

export const minutesToClock = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

export const formatAmpm = (minutes: number): string => {
  const hours = Math.floor(minutes / 60) % 24
  const mins = minutes % 60
  const suffix = hours < 12 ? 'am' : 'pm'
  const hour12 = hours % 12 || 12
  return `${hour12}:${String(mins).padStart(2, '0')} ${suffix}`
}

export const formatSeriesTime = (value: string): string => {
  const minutes = parseClock(value)
  if (minutes == null || minutes > 24 * 60) return value
  if (minutes === 24 * 60) return '12:00 am'
  return formatAmpm(minutes)
}

export const nthChoices = (iso: string): NthChoice[] => {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return []
  const weekday = weekdayOf(iso)
  const occurrence = Math.ceil(day / 7)
  const daysInMonth = new Date(year, month, 0).getDate()
  const isLast = day + 7 > daysInMonth
  const choices: NthChoice[] = []
  if (occurrence >= 1 && occurrence <= 4) {
    choices.push({
      key: 'nth',
      label: `${EVERY_WORD[occurrence - 1] === 'every' ? '1st' : EVERY_WORD[occurrence - 1]} ${WEEKDAY_LONG[weekday]}`,
      nth: occurrence,
      weekday,
    })
  }
  if (isLast) {
    choices.push({
      key: 'last',
      label: `Last ${WEEKDAY_LONG[weekday]}`,
      nth: 5,
      weekday,
    })
  }
  return choices
}

export const alignWeeklyStart = (iso: string, weekdays: Weekday[]): string => {
  if (!weekdays.length) return iso
  const selected = new Set(weekdays)
  let cursor = iso
  for (let step = 0; step < 7; step += 1) {
    if (selected.has(weekdayOf(cursor))) return cursor
    cursor = addDays(cursor, 1)
  }
  return iso
}

export const isPastSeries = (row: AvailabilitySeries, today: string): boolean => {
  if (row.repeat.type === 'none') return row.start_date < today
  return !!row.end_date && row.end_date < today
}

export const rowLock = (row: AvailabilitySeries, rows: AvailabilitySeries[]): RowLock => {
  const child = rows.find((item) => item.split_parent_id === row.id)
  const parent =
    row.split_parent_id == null
      ? undefined
      : rows.find((item) => item.id === row.split_parent_id)
  const splitMembers = [row, child, parent].filter((item): item is AvailabilitySeries => !!item)
  const inSplit = child != null || row.split_parent_id != null
  if (inSplit && splitMembers.some((item) => item.sync_status !== 'synced')) return 'busy'
  if (row.pending_action === 'delete') return 'busy'
  if (row.read_only_reason === 'multi_provider') return 'readonly'
  if (row.read_only_reason === 'unknown_repeat') return 'unknown'
  return 'open'
}

export const canRetrySeries = (row: AvailabilitySeries): boolean =>
  row.sync_status === 'sync_failed' && row.split_parent_id == null

export const syncLabel = (row: AvailabilitySeries): string | null => {
  if (row.sync_status === 'sync_failed') return 'Could not save to the EMR'
  if (row.sync_status !== 'pending') return null
  if (row.pending_action === 'create') return 'Creating…'
  if (row.pending_action === 'delete') return 'Deleting…'
  return 'Saving to EMR…'
}

export const seriesTimeLabel = (row: AvailabilitySeries): string => {
  if (row.all_day) return 'All day'
  return `${formatSeriesTime(row.start_time)} – ${formatSeriesTime(row.end_time)}`
}

export const seriesRunsLabel = (row: AvailabilitySeries): string => {
  if (row.repeat.type === 'none') return formatMdy(row.start_date)
  const end = row.end_date ? formatMdy(row.end_date) : 'no end'
  return `${formatMdy(row.start_date)} → ${end}`
}

const clampEvery = (value: number): number => (value >= 1 ? value : 1)

export const rowToDraft = (row: AvailabilitySeries): BlockDraft => {
  const start = parseClock(row.start_time)
  const end = parseClock(row.end_time)
  const legacyTime =
    !row.all_day && (start == null || end == null || end <= start || end > 24 * 60)
  const repeat = row.repeat
  const base: BlockDraft = {
    id: row.id,
    userId: row.user_id,
    kindId: row.kind.id,
    title: row.title,
    allDay: row.all_day,
    date: row.start_date,
    startMinutes: start != null && start < 24 * 60 ? start : 8 * 60,
    duration: !legacyTime && start != null && end != null ? end - start : 60,
    repeat: 'none',
    every: 1,
    unit: 'week',
    weekdays: [weekdayOf(row.start_date)],
    until: repeat.type === 'none' ? null : row.end_date,
    pref: [],
    legacyTime,
  }
  if (repeat.type === 'weekly_days') {
    return { ...base, repeat: 'weekly_days', weekdays: repeat.weekdays }
  }
  if (repeat.type === 'nth_weekday') {
    return {
      ...base,
      repeat: 'every',
      every: clampEvery(repeat.every_months),
      unit: repeat.nth === 5 ? 'last' : 'nth',
      weekdays: [repeat.weekday],
    }
  }
  if (repeat.type === 'every') {
    return {
      ...base,
      repeat: 'every',
      every: clampEvery(repeat.every),
      unit: repeat.unit,
    }
  }
  return base
}

export const blankDraft = (userId: number, date: string, kindId: number | null, kindName: string): BlockDraft => ({
  id: null,
  userId,
  kindId,
  title: kindName,
  allDay: false,
  date,
  startMinutes: 8 * 60,
  duration: 60,
  repeat: 'none',
  every: 1,
  unit: 'week',
  weekdays: [weekdayOf(date)],
  until: null,
  pref: [],
  legacyTime: false,
})

export const applyDraftPatch = (draft: BlockDraft, patch: Partial<BlockDraft>): BlockDraft => {
  const next: BlockDraft = { ...draft, ...patch }
  if (patch.startMinutes != null || patch.duration != null || patch.allDay != null) {
    next.legacyTime = false
  }
  if (next.repeat === 'weekly_days') {
    if (next.every > 4) next.every = 4
    if (next.weekdays.length) next.date = alignWeeklyStart(next.date, next.weekdays)
  }
  if (next.repeat === 'every' && (next.unit === 'nth' || next.unit === 'last')) {
    const choices = nthChoices(next.date)
    if (!choices.some((choice) => choice.key === next.unit)) next.unit = 'week'
  }
  if (next.repeat === 'none') next.until = null
  return next
}

const dayList = (days: Weekday[]): string => {
  const ordered = CHECKBOX_ORDER.filter((day) => days.includes(day))
  if (ordered.join(',') === 'mon,tue,wed,thu,fri') return 'Mon–Fri'
  return ordered.map((day) => WEEKDAY_SHORT[day]).join(' ')
}

export const draftSummary = (draft: BlockDraft): string => {
  const start = `starting ${formatMdy(draft.date)}`
  const until = draft.until ? `until ${formatMdy(draft.until)}` : 'until an end date'
  if (draft.repeat === 'none') return `Once on ${formatMdy(draft.date)}`
  if (draft.repeat === 'weekly_days') {
    if (!draft.weekdays.length) return 'Pick at least one day'
    const cadence = draft.every <= 1 ? '' : `Every ${draft.every} weeks, `
    return `${cadence}${dayList(draft.weekdays)}, ${start} ${until}`
  }
  if (draft.unit === 'nth' || draft.unit === 'last') {
    const choice = nthChoices(draft.date).find((item) => item.key === draft.unit)
    const name = choice?.label ?? 'That weekday'
    const cadence = draft.every === 1 ? 'every month' : `every ${draft.every} months`
    return `${name} ${cadence}, ${start} ${until}`
  }
  const unit = draft.unit
  let cadence = 'Every week'
  if (unit === 'day') cadence = draft.every === 1 ? 'Every day' : `Every ${draft.every} days`
  if (unit === 'workday') {
    cadence = draft.every === 1 ? 'Every working day (Monday–Friday)' : `Every ${draft.every} working days`
  }
  if (unit === 'week') cadence = draft.every === 1 ? 'Every week' : `Every ${draft.every} weeks`
  if (unit === 'month') cadence = draft.every === 1 ? 'Every month' : `Every ${draft.every} months`
  if (unit === 'year') cadence = draft.every === 1 ? 'Every year' : `Every ${draft.every} years`
  return `${cadence}, ${start} ${until}`
}

export const draftEndsLabel = (draft: BlockDraft): string => {
  if (draft.allDay) return 'All day'
  const end = draft.startMinutes + draft.duration
  if (end > 24 * 60) return 'Ends after midnight'
  return `Ends ${formatAmpm(end === 24 * 60 ? 0 : end)}`
}

export type DraftWriteResult =
  | { ok: true; body: AvailabilityWriteBody }
  | { ok: false; fields: Record<string, string> }

export const draftToWriteBody = (draft: BlockDraft): DraftWriteResult => {
  const fields: Record<string, string> = {}
  if (draft.kindId == null) fields.kind = 'Choose a category'
  if (draft.legacyTime) fields.start_time = 'Pick a start and duration that stay inside the day'
  if (draft.title.length > 150) fields.title = 'Title must be 150 characters or less'

  let repeat: AvailabilityRepeat = { type: 'none' }
  let date = draft.date
  if (draft.repeat === 'weekly_days') {
    if (!draft.weekdays.length) fields.weekdays = 'Pick at least one day'
    else if (draft.every > 1) fields.weekdays = 'Days of the week repeat every week'
    date = alignWeeklyStart(draft.date, draft.weekdays)
    repeat = { type: 'weekly_days', weekdays: draft.weekdays }
  } else if (draft.repeat === 'every') {
    if (draft.unit === 'nth' || draft.unit === 'last') {
      const choice = nthChoices(draft.date).find((item) => item.key === draft.unit)
      if (!choice) fields.repeat = 'Pick a repeat that matches this date'
      else {
        repeat = {
          type: 'nth_weekday',
          nth: choice.nth,
          weekday: choice.weekday,
          every_months: draft.every,
        }
      }
    } else {
      repeat = { type: 'every', every: draft.every, unit: draft.unit }
    }
  }

  const repeating = repeat.type !== 'none'
  if (repeating && !draft.until) fields.end_date = 'A repeating series needs an end date'
  if (repeating && draft.until && draft.until < date) {
    fields.end_date = 'The end date cannot be before the start date'
  }

  const end = draft.startMinutes + draft.duration
  if (!draft.allDay && !draft.legacyTime) {
    if (draft.duration < 5) fields.end_time = 'Duration must be at least 5 minutes'
    if (end > 24 * 60) fields.end_time = 'A block cannot go past midnight'
  }

  if (Object.keys(fields).length) return { ok: false, fields }

  const body: AvailabilityWriteBody = {
    user_id: draft.userId,
    kind: draft.kindId as number,
    start_date: date,
    end_date: repeating ? draft.until : null,
    all_day: draft.allDay,
    repeat,
  }
  const title = draft.title.trim()
  if (title) body.title = title
  if (!draft.allDay) {
    body.start_time = minutesToClock(draft.startMinutes)
    body.end_time = minutesToClock(end)
  }
  return { ok: true, body }
}

const errorBody = (error: unknown): AvailabilityErrorBody => {
  if (!error || typeof error !== 'object' || !('data' in error)) return {}
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return {}
  return data as AvailabilityErrorBody
}

export const availabilityErrorText = (
  error: unknown,
): { message: string; fields: Record<string, string>; code?: string } => {
  const body = errorBody(error)
  const status = error && typeof error === 'object' && 'status' in error ? Number((error as { status?: number }).status) : 0
  const fields: Record<string, string> = {}
  Object.entries(body.errors ?? {}).forEach(([key, messages]) => {
    if (messages[0]) fields[key] = messages[0]
  })
  if (status === 401) return { message: 'Please log in again.', fields, code: body.code }
  if (!body.code && status === 403) {
    return { message: 'Please choose a clinic and try again.', fields }
  }
  const message =
    (body.code && ERROR_TEXT[body.code]) ||
    body.message ||
    'Something went wrong. Please try again.'
  return { message, fields, code: body.code }
}
